import React, { useState, useEffect } from "react";
import { 
  Table, 
  Card, 
  Select, 
  DatePicker, 
  Button, 
  Space, 
  Statistic, 
  Row, 
  Col,
  Tag,
  Tooltip,
  Image,
  Modal
} from "antd";
import { 
  EyeOutlined, 
  CalendarOutlined, 
  CarOutlined,
  DollarOutlined,
  ThunderboltOutlined
} from "@ant-design/icons";
import { collection, query, where, orderBy, onSnapshot } from "firebase/firestore";
import { firestore } from "../../firebase";
import { useAuth } from "../Context";
import VehiculosSelect from "./VehiculosSelect";
import moment from "moment";
import Title from "antd/es/typography/Title";

const { RangePicker } = DatePicker;

const HistorialCargas = () => {
  const [cargas, setCargas] = useState([]);
  const [vehiculos, setVehiculos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroVehiculo, setFiltroVehiculo] = useState("");
  const [filtroFecha, setFiltroFecha] = useState([]);
  const [imageModalVisible, setImageModalVisible] = useState(false);
  const [selectedImage, setSelectedImage] = useState("");
  const { sucursal } = useAuth();

  useEffect(() => {
    if (!sucursal) return;

    // Cargar cargas
    const cargasQuery = query(
      collection(firestore, `sucursal/${sucursal}/carga`),
      orderBy("fecha", "desc")
    );

    const unsubscribeCargas = onSnapshot(cargasQuery, (snapshot) => {
      const cargasData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        fecha: doc.data().fecha?.toDate()
      }));
      setCargas(cargasData);
      setLoading(false);
    });

    // Cargar vehículos para mostrar nombres
    const vehiculosQuery = collection(firestore, `sucursal/${sucursal}/vehiculo`);
    const unsubscribeVehiculos = onSnapshot(vehiculosQuery, (snapshot) => {
      const vehiculosData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setVehiculos(vehiculosData);
    });

    return () => {
      unsubscribeCargas();
      unsubscribeVehiculos();
    };
  }, [sucursal]);

  // Filtrar cargas
  const cargasFiltradas = cargas.filter(carga => {
    const cumpleFiltroVehiculo = !filtroVehiculo || carga.vehiculoId === filtroVehiculo;
    
    const cumpleFiltroFecha = !filtroFecha.length || 
      (carga.fecha >= filtroFecha[0].toDate() && carga.fecha <= filtroFecha[1].toDate());
    
    return cumpleFiltroVehiculo && cumpleFiltroFecha;
  });

  // Calcular estadísticas
  const estadisticas = {
    totalCargas: cargasFiltradas.length,
    totalGastado: cargasFiltradas.reduce((sum, carga) => sum + carga.precio, 0),
    totalLitros: cargasFiltradas.reduce((sum, carga) => sum + carga.litros, 0),
    totalKmRecorridos: cargasFiltradas.reduce((sum, carga) => sum + carga.kmRecorridos, 0),
    rendimientoPromedio: cargasFiltradas.length > 0 
      ? cargasFiltradas.reduce((sum, carga) => sum + carga.rendimiento, 0) / cargasFiltradas.length
      : 0
  };

  const getVehiculoNombre = (vehiculoId) => {
    const vehiculo = vehiculos.find(v => v.id === vehiculoId);
    return vehiculo ? `${vehiculo.marca} ${vehiculo.modelo} - ${vehiculo.placas}` : vehiculoId;
  };

  const columns = [
    {
      title: "Fecha",
      dataIndex: "fecha",
      key: "fecha",
      render: (fecha) => moment(fecha).format("DD/MM/YYYY"),
      sorter: (a, b) => new Date(a.fecha) - new Date(b.fecha),
    },
    {
      title: "Vehículo",
      dataIndex: "vehiculoId",
      key: "vehiculo",
      render: (vehiculoId) => getVehiculoNombre(vehiculoId),
    },
    {
      title: "Kilometraje",
      key: "kilometraje",
      render: (_, record) => (
        <div>
          <div>Inicial: {record.kilometrajeInicial.toLocaleString()} km</div>
          <div>Final: {record.kilometrajeFinal.toLocaleString()} km</div>
          <Tag color="blue">{record.kmRecorridos} km recorridos</Tag>
        </div>
      ),
    },
    {
      title: "Combustible",
      key: "combustible",
      render: (_, record) => (
        <div>
          <div>{record.litros} L</div>
          <div>${record.precioPorLitro?.toFixed(2)}/L</div>
        </div>
      ),
    },
    {
      title: "Precio Total",
      dataIndex: "precio",
      key: "precio",
      render: (precio) => `$${precio.toFixed(2)}`,
      sorter: (a, b) => a.precio - b.precio,
    },
    {
      title: "Rendimiento",
      dataIndex: "rendimiento",
      key: "rendimiento",
      render: (rendimiento) => (
        <Tag color={rendimiento > 10 ? "green" : rendimiento > 8 ? "orange" : "red"}>
          {rendimiento?.toFixed(2)} km/L
        </Tag>
      ),
      sorter: (a, b) => a.rendimiento - b.rendimiento,
    },
    {
      title: "Ticket",
      dataIndex: "ticketUrl",
      key: "ticket",
      render: (ticketUrl) => (
        ticketUrl ? (
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => {
              setSelectedImage(ticketUrl);
              setImageModalVisible(true);
            }}
          >
            Ver
          </Button>
        ) : "No disponible"
      ),
    },
  ];

  const limpiarFiltros = () => {
    setFiltroVehiculo("");
    setFiltroFecha([]);
  };

  return (
    <div style={{ padding: "20px" }}>
      <Title level={3}>Historial de Cargas de Combustible</Title>
      
      {/* Estadísticas */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={4}>
          <Card size="small">
            <Statistic
              title="Total Cargas"
              value={estadisticas.totalCargas}
              prefix={<ThunderboltOutlined />}
            />
          </Card>
        </Col>
        <Col span={5}>
          <Card size="small">
            <Statistic
              title="Total Gastado"
              value={estadisticas.totalGastado}
              prefix={<DollarOutlined />}
              precision={2}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card size="small">
            <Statistic
              title="Total Litros"
              value={estadisticas.totalLitros}
              suffix="L"
              precision={1}
            />
          </Card>
        </Col>
        <Col span={5}>
          <Card size="small">
            <Statistic
              title="Km Recorridos"
              value={estadisticas.totalKmRecorridos}
              suffix="km"
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="Rendimiento Promedio"
              value={estadisticas.rendimientoPromedio}
              suffix="km/L"
              precision={2}
            />
          </Card>
        </Col>
      </Row>

      {/* Filtros */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={16} align="middle">
          <Col span={6}>
            <VehiculosSelect
              value={filtroVehiculo}
              onChange={setFiltroVehiculo}
              placeholder="Filtrar por vehículo"
            />
          </Col>
          <Col span={8}>
            <RangePicker
              value={filtroFecha}
              onChange={setFiltroFecha}
              format="DD/MM/YYYY"
              placeholder={["Fecha inicio", "Fecha fin"]}
              style={{ width: "100%" }}
            />
          </Col>
          <Col span={4}>
            <Button onClick={limpiarFiltros}>
              Limpiar Filtros
            </Button>
          </Col>
          <Col span={6}>
            <Space>
              <span>Total filtrado: {cargasFiltradas.length} cargas</span>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Tabla */}
      <Table
        columns={columns}
        dataSource={cargasFiltradas}
        rowKey="id"
        loading={loading}
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => 
            `${range[0]}-${range[1]} de ${total} registros`,
        }}
        scroll={{ x: 1000 }}
      />

      {/* Modal para ver imagen del ticket */}
      <Modal
        title="Ticket de Combustible"
        open={imageModalVisible}
        onCancel={() => setImageModalVisible(false)}
        footer={null}
        width={600}
      >
        <Image
          src={selectedImage}
          alt="Ticket de combustible"
          style={{ width: "100%" }}
        />
      </Modal>
    </div>
  );
};

export default HistorialCargas;
